<template>
  <div class="modal-back" @click.self="$emit('close')">
    <div class="modal card wide">
      <div class="head">
        <h3>My account</h3>
        <button class="secondary small" @click="$emit('close')">Close</button>
      </div>

      <div class="tabs">
        <button :class="{tab:1, active: tab==='profile'}"  @click="tab='profile'"><i class="mdi mdi-account-circle"></i> Profile</button>
        <button :class="{tab:1, active: tab==='password'}" @click="tab='password'"><i class="mdi mdi-key"></i> Password</button>
        <button :class="{tab:1, active: tab==='sessions'}" @click="loadSessions"><i class="mdi mdi-devices"></i> Sessions</button>
      </div>

      <!-- PROFILE -->
      <div v-if="tab === 'profile'">
        <label>Name</label><input v-model="profile.name" />
        <label>Phone</label><input v-model="profile.phone" placeholder="0712345678" />
        <label>Email (used to sign in)</label><input v-model="profile.email" type="email" />
        <div class="actions">
          <button class="primary" @click="saveProfile" :disabled="savingProfile">{{ savingProfile ? 'Saving…' : 'Save profile' }}</button>
        </div>
        <p :class="profOk ? 'ok' : 'err'" v-if="profMsg">{{ profMsg }}</p>
      </div>

      <!-- PASSWORD -->
      <div v-if="tab === 'password'">
        <label>Current password</label><input v-model="pw.old" type="password" autocomplete="current-password" />
        <label>New password (min 8 chars)</label><input v-model="pw.new" type="password" autocomplete="new-password" />
        <label>Confirm new password</label><input v-model="pw.confirm" type="password" autocomplete="new-password" />
        <p class="muted small">Changing your password will sign you out of any other devices.</p>
        <div class="actions">
          <button class="primary" @click="changePassword" :disabled="!pwValid || changingPw">
            {{ changingPw ? 'Changing…' : 'Change password' }}
          </button>
        </div>
        <p :class="pwOk ? 'ok' : 'err'" v-if="pwMsg">{{ pwMsg }}</p>
      </div>

      <!-- SESSIONS -->
      <div v-if="tab === 'sessions'">
        <p class="muted small">All the devices where your account is currently signed in.</p>
        <table v-if="sessions.length" class="mini-table">
          <thead><tr><th>Method</th><th>Device / IP</th><th>Created</th><th>Last active</th><th></th></tr></thead>
          <tbody>
            <tr v-for="s in sessions" :key="s.id" :class="{ current: s.is_current }">
              <td>{{ s.login_method }}</td>
              <td class="muted small">{{ s.ip_address || '?' }}<br />{{ (s.device_info || '').slice(0, 60) }}</td>
              <td class="muted small">{{ s.created_at }}</td>
              <td class="muted small">{{ s.last_active_at || '—' }}</td>
              <td>
                <span v-if="s.is_current" class="chip current-chip">this device</span>
                <button v-else class="danger small" @click="revoke(s)">Revoke</button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No active sessions?</p>
      </div>
    </div>
  </div>
</template>

<script>
import { paysol, execute } from '@/api';

export default {
  name: 'ProfileModal',
  emits: ['close', 'signout'],
  data() {
    return {
      tab: 'profile',
      profile: { name: sessionStorage.getItem('userName') || '', phone: '', email: '' },
      savingProfile: false, profMsg: '', profOk: false,
      pw: { old: '', new: '', confirm: '' }, changingPw: false, pwMsg: '', pwOk: false,
      sessions: [],
    };
  },
  computed: {
    pwValid() { return this.pw.old && this.pw.new && this.pw.new.length >= 8 && this.pw.new === this.pw.confirm; }
  },
  async mounted() {
    // Prefill profile from getMe
    try {
      const { data } = await execute({ function: 'getMe' }, paysol);
      if (data.success) this.profile = { name: data.data.user.name, phone: data.data.user.phone || '', email: data.data.user.email || '' };
    } catch (e) { /* silent */ }
  },
  methods: {
    async saveProfile() {
      this.savingProfile = true; this.profMsg = '';
      try {
        const { data } = await execute({ function: 'updateMyProfile', ...this.profile }, paysol);
        this.profOk = data.success; this.profMsg = data.success ? 'Saved.' : ('Failed: ' + data.message);
        if (data.success) sessionStorage.setItem('userName', data.data.name);
      } finally { this.savingProfile = false; }
    },
    async changePassword() {
      this.changingPw = true; this.pwMsg = '';
      try {
        const { data } = await execute({ function: 'changePassword', oldPassword: this.pw.old, newPassword: this.pw.new }, paysol);
        this.pwOk = data.success; this.pwMsg = data.success ? data.message : ('Failed: ' + data.message);
        if (data.success) this.pw = { old: '', new: '', confirm: '' };
      } finally { this.changingPw = false; }
    },
    async loadSessions() {
      this.tab = 'sessions';
      const { data } = await execute({ function: 'listMySessions' }, paysol);
      if (data.success) this.sessions = data.data;
    },
    async revoke(s) {
      if (!confirm('Revoke this session? The device will be signed out immediately.')) return;
      const { data } = await execute({ function: 'revokeSession', id: s.id }, paysol);
      if (data.success) await this.loadSessions();
      else alert(data.message);
    },
  }
};
</script>

<style scoped>
.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 60; padding: 24px; }
.modal { width: 100%; max-width: 620px; max-height: 90vh; overflow-y: auto; }
.head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.head h3 { margin: 0; color: var(--paysol-teal); }
.tabs { display: flex; gap: 4px; padding-bottom: 8px; margin-bottom: 16px; border-bottom: 1px solid var(--paysol-line); }
button.tab { background: transparent; color: var(--paysol-muted); padding: 8px 14px; border-radius: 6px 6px 0 0; font-size: 12px; display: inline-flex; align-items: center; gap: 6px; border-bottom: 2px solid transparent; }
button.tab.active { color: var(--paysol-teal); border-bottom-color: var(--paysol-teal); }
.actions { margin-top: 16px; }
.mini-table { font-size: 12px; }
tr.current td { background: var(--paysol-teal-glow); }
.current-chip { background: var(--paysol-teal-glow); color: var(--paysol-teal); padding: 2px 8px; border-radius: 3px; font-size: 10px; font-weight: 700; }
.muted { color: var(--paysol-muted); } .small { font-size: 11px; }
.center { text-align: center; padding: 20px 0; }
.ok { color: var(--paysol-success); font-size: 12px; margin-top: 8px; }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
button.small { padding: 4px 10px; font-size: 11px; }
</style>
